import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Upload,
  FileText,
  X,
  Minus,
  Plus,
  Loader2,
  CheckCircle2,
  ShieldCheck,
  Lock,
  ArrowLeft,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SquareCard } from "@/components/SquareCard";
import { api, gbp, formatApiErrorDetail } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function PlaceOrder() {
  const { customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [selections, setSelections] = useState({}); // key -> {checked, quantity}
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [success, setSuccess] = useState(null);
  const [discountInput, setDiscountInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null); // {code, percent}
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState(null); // null | 'uk' | 'europe'
  const [deliveryAddress, setDeliveryAddress] = useState({ line1: "", city: "", postcode: "", country: "" });

  const fileInputRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/config").then((res) => {
      setConfig(res.data);
      const init = {};
      res.data.services.forEach((s) => (init[s.key] = { checked: false, quantity: 1 }));
      setSelections(init);
    });
  }, []);

  // Require sign-in; prefill from account
  useEffect(() => {
    if (customer === false) {
      navigate("/login");
    } else if (customer) {
      setForm((f) => ({ ...f, name: f.name || customer.name || "", email: customer.email || "" }));
    }
  }, [customer, navigate]);

  const services = config?.services || [];

  const total = useMemo(() => {
    return services.reduce((sum, s) => {
      const sel = selections[s.key];
      if (sel?.checked) return sum + s.price * (sel.quantity || 0);
      return sum;
    }, 0);
  }, [services, selections]);

  const discountAmount = appliedDiscount ? Math.round(total * appliedDiscount.percent) / 100 : 0;
  const deliveryOptions = config?.delivery_options || [];
  const deliveryFee = deliveryMethod
    ? deliveryOptions.find((d) => d.key === deliveryMethod)?.fee || 0
    : 0;
  const finalTotal = Math.round((total - discountAmount + deliveryFee) * 100) / 100;

  const toggleDelivery = (key) => setDeliveryMethod((cur) => (cur === key ? null : key));

  const applyDiscount = async () => {
    const code = discountInput.trim();
    if (!code) return;
    setApplyingDiscount(true);
    try {
      const res = await api.post("/discounts/validate", { code });
      setAppliedDiscount({ code: res.data.code, percent: res.data.percent });
      toast.success(`Code ${res.data.code} applied — ${res.data.percent}% off!`);
    } catch (e) {
      setAppliedDiscount(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally {
      setApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountInput("");
  };

  const selectedList = services.filter((s) => selections[s.key]?.checked);

  const toggleService = (key, checked) =>
    setSelections((p) => ({ ...p, [key]: { ...p[key], checked } }));

  const setQty = (key, delta) =>
    setSelections((p) => ({
      ...p,
      [key]: { ...p[key], quantity: Math.max(1, (p[key]?.quantity || 1) + delta) },
    }));

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setUploading(true);
    for (const f of arr) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, res.data]);
      } catch (e) {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name";
    if (!form.phone.trim()) return "Please enter your phone number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email";
    if (!selectedList.length) return "Please select at least one service";
    if (deliveryMethod) {
      if (!deliveryAddress.line1.trim() || !deliveryAddress.city.trim() || !deliveryAddress.postcode.trim())
        return "Please enter your delivery address (address, city and postcode)";
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      let sourceId = null;
      if (config?.square_enabled) {
        sourceId = await cardRef.current.tokenize();
      }
      const payload = {
        ...form,
        services: selectedList.map((s) => ({
          key: s.key,
          quantity: selections[s.key].quantity,
        })),
        file_ids: files.map((f) => f.id),
        instructions,
        source_id: sourceId,
        discount_code: appliedDiscount?.code || null,
        delivery_method: deliveryMethod,
        delivery_address: deliveryMethod ? deliveryAddress : null,
      };
      const res = await api.post("/orders", payload);
      setSuccess(res.data);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="App">
        <SiteHeader />
        <div className="mx-auto flex max-w-2xl flex-col items-center px-6 py-24 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex h-20 w-20 items-center justify-center bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
          >
            <CheckCircle2 size={44} />
          </motion.div>
          <h1 className="mt-8 font-display text-4xl font-extrabold tracking-tight" data-testid="order-success">
            Order confirmed!
          </h1>
          <p className="mt-3 text-lg text-[var(--text-secondary)]">
            Thank you, {form.name}. We've received your order and payment.
          </p>
          <div className="mt-8 w-full border border-black/10 bg-white p-6 text-left">
            <div className="flex justify-between border-b border-black/10 pb-3">
              <span className="text-[var(--text-secondary)]">Order number</span>
              <span className="font-mono font-semibold">{success.order_number}</span>
            </div>
            <div className="flex justify-between pt-3">
              <span className="text-[var(--text-secondary)]">Total paid</span>
              <span className="font-display text-xl font-bold text-[var(--brand-primary)]">
                {gbp(success.total)}
              </span>
            </div>
            {success.payment_mode === "simulated" && (
              <p className="mt-4 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
                Payment was simulated (test mode). Add live Square keys to take real payments.
              </p>
            )}
          </div>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[var(--brand-primary)]"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="App" data-testid="order-page">
      <SiteHeader />

      <div className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-primary)]">
            Place an Order
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            Tell us what you need.
          </h1>
          <p className="mt-3 max-w-xl text-lg text-[var(--text-secondary)]">
            Fill in your details, upload your files and select services. Your total updates
            live as you go.
          </p>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 lg:grid-cols-3 lg:px-10">
        {/* LEFT: form */}
        <div className="space-y-8 lg:col-span-2">
          {/* Customer details */}
          <section className="border border-black/10 bg-white p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold">1. Your details</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Smith"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone number</Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="07123 456789"
                  className="mt-2"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  readOnly
                  placeholder="jane@example.com"
                  className="mt-2 bg-black/5"
                />
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  Linked to your account — orders appear under “My Orders”.
                </p>
              </div>
            </div>
          </section>

          {/* File upload */}
          <section className="border border-black/10 bg-white p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold">2. Upload your files</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Images, PDFs or documents you need processed (max 25MB each).
            </p>
            <div
              data-testid="file-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFiles(e.dataTransfer.files);
              }}
              className={`mt-5 flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-10 text-center transition-colors ${
                dragOver
                  ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5"
                  : "border-black/15 hover:border-[var(--brand-primary)]"
              }`}
            >
              <Upload size={28} className="text-[var(--brand-primary)]" />
              <p className="mt-3 text-sm font-medium">
                Drag & drop files here, or <span className="text-[var(--brand-primary)]">browse</span>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                data-testid="file-input"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
            {uploading && (
              <p className="mt-3 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                <Loader2 className="animate-spin" size={14} /> Uploading…
              </p>
            )}
            {files.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="file-list">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between border border-black/10 bg-[#F9F9F8] px-4 py-3"
                  >
                    <span className="flex items-center gap-3 truncate">
                      <FileText size={18} className="shrink-0 text-[var(--brand-primary)]" />
                      <span className="truncate text-sm">{f.original_filename}</span>
                    </span>
                    <button
                      onClick={() => removeFile(f.id)}
                      data-testid={`remove-file-${f.id}`}
                      className="text-[var(--text-secondary)] hover:text-red-600"
                    >
                      <X size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Services */}
          <section className="border border-black/10 bg-white p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold">3. Select services</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Tick a service and set the quantity (pages / copies).
            </p>
            <div className="mt-6 space-y-3">
              {services.map((s) => {
                const sel = selections[s.key] || {};
                return (
                  <div
                    key={s.key}
                    data-testid={`service-row-${s.key}`}
                    className={`flex flex-wrap items-center justify-between gap-4 border p-4 transition-colors ${
                      sel.checked ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-black/10"
                    }`}
                  >
                    <label className="flex flex-1 cursor-pointer items-center gap-3">
                      <Checkbox
                        checked={!!sel.checked}
                        data-testid={`service-checkbox-${s.key}`}
                        onCheckedChange={(v) => toggleService(s.key, v)}
                      />
                      <span>
                        <span className="font-semibold">{s.label}</span>
                        <span className="ml-2 font-mono text-sm text-[var(--text-secondary)]">
                          {gbp(s.price)} / unit
                        </span>
                      </span>
                    </label>

                    <div className="flex items-center gap-3">
                      <div
                        className={`flex items-center border border-black/15 ${
                          sel.checked ? "opacity-100" : "opacity-40"
                        }`}
                      >
                        <button
                          disabled={!sel.checked}
                          data-testid={`qty-minus-${s.key}`}
                          onClick={() => setQty(s.key, -1)}
                          className="px-2 py-1.5 hover:bg-black/5 disabled:cursor-not-allowed"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-10 text-center font-mono text-sm" data-testid={`qty-value-${s.key}`}>
                          {sel.quantity || 1}
                        </span>
                        <button
                          disabled={!sel.checked}
                          data-testid={`qty-plus-${s.key}`}
                          onClick={() => setQty(s.key, 1)}
                          className="px-2 py-1.5 hover:bg-black/5 disabled:cursor-not-allowed"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="w-16 text-right font-mono text-sm font-semibold">
                        {gbp(sel.checked ? s.price * (sel.quantity || 1) : 0)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Delivery */}
          <section className="border border-black/10 bg-white p-6 sm:p-8">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold">
              <Truck size={22} className="text-[var(--brand-primary)]" /> 4. Delivery
            </h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Optional — leave both unticked to collect from our Victoria Road shop.
            </p>
            <div className="mt-5 space-y-3">
              {deliveryOptions.map((d) => (
                <label
                  key={d.key}
                  data-testid={`delivery-row-${d.key}`}
                  className={`flex cursor-pointer items-center justify-between gap-4 border p-4 transition-colors ${
                    deliveryMethod === d.key
                      ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5"
                      : "border-black/10"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Checkbox
                      checked={deliveryMethod === d.key}
                      data-testid={`delivery-checkbox-${d.key}`}
                      onCheckedChange={() => toggleDelivery(d.key)}
                    />
                    <span className="font-semibold">{d.label}</span>
                  </span>
                  <span className="font-mono text-sm font-semibold">+{gbp(d.fee)}</span>
                </label>
              ))}
            </div>

            {deliveryMethod && (
              <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="delivery-address">
                <div className="sm:col-span-2">
                  <Label htmlFor="addr1">Address</Label>
                  <Input
                    id="addr1"
                    data-testid="delivery-line1"
                    value={deliveryAddress.line1}
                    onChange={(e) => setDeliveryAddress({ ...deliveryAddress, line1: e.target.value })}
                    placeholder="Street address"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="addrcity">City / Town</Label>
                  <Input
                    id="addrcity"
                    data-testid="delivery-city"
                    value={deliveryAddress.city}
                    onChange={(e) => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="addrpost">Postcode</Label>
                  <Input
                    id="addrpost"
                    data-testid="delivery-postcode"
                    value={deliveryAddress.postcode}
                    onChange={(e) => setDeliveryAddress({ ...deliveryAddress, postcode: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="addrcountry">Country</Label>
                  <Input
                    id="addrcountry"
                    data-testid="delivery-country"
                    value={deliveryAddress.country}
                    onChange={(e) => setDeliveryAddress({ ...deliveryAddress, country: e.target.value })}
                    placeholder={deliveryMethod === "europe" ? "e.g. France, Germany…" : "United Kingdom"}
                    className="mt-2"
                  />
                </div>
              </div>
            )}
          </section>

          {/* Instructions */}
          <section className="border border-black/10 bg-white p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold">5. Special instructions</h2>
            <Textarea
              data-testid="input-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Any notes about paper type, colour, sizing, deadlines…"
              rows={4}
              className="mt-4"
            />
          </section>
        </div>

        {/* RIGHT: summary + payment */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-5">
            <div className="border border-black/10 bg-white p-6">
              <h2 className="font-display text-xl font-bold">Order summary</h2>
              <div className="mt-4 space-y-2 text-sm">
                {selectedList.length === 0 && (
                  <p className="text-[var(--text-secondary)]">No services selected yet.</p>
                )}
                {selectedList.map((s) => (
                  <div key={s.key} className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">
                      {s.label} × {selections[s.key].quantity}
                    </span>
                    <span className="font-mono">
                      {gbp(s.price * selections[s.key].quantity)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Discount code */}
              <div className="mt-4 border-t border-black/10 pt-4">
                {appliedDiscount ? (
                  <div
                    className="flex items-center justify-between border border-green-200 bg-green-50 px-3 py-2 text-sm"
                    data-testid="applied-discount"
                  >
                    <span className="font-medium text-green-800">
                      {appliedDiscount.code} · {appliedDiscount.percent}% off
                    </span>
                    <button
                      onClick={removeDiscount}
                      data-testid="remove-discount"
                      className="text-green-800/70 hover:text-green-900"
                    >
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && applyDiscount()}
                      placeholder="Discount code"
                      data-testid="discount-input"
                      className="flex-1 border border-black/15 px-3 py-2 text-sm uppercase outline-none focus:border-[var(--brand-primary)]"
                    />
                    <button
                      onClick={applyDiscount}
                      disabled={applyingDiscount || !discountInput.trim()}
                      data-testid="apply-discount"
                      className="bg-[var(--brand-ink)] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                    >
                      {applyingDiscount ? <Loader2 className="animate-spin" size={15} /> : "Apply"}
                    </button>
                  </div>
                )}
              </div>

              {(appliedDiscount || deliveryMethod) && (
                <div className="mt-4 space-y-1.5 text-sm">
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span>Subtotal</span>
                    <span className="font-mono">{gbp(total)}</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex justify-between text-green-700">
                      <span>Discount ({appliedDiscount.percent}%)</span>
                      <span className="font-mono" data-testid="discount-amount">−{gbp(discountAmount)}</span>
                    </div>
                  )}
                  {deliveryMethod && (
                    <div className="flex justify-between text-[var(--text-secondary)]">
                      <span>Delivery ({deliveryMethod === "uk" ? "UK" : "Europe"})</span>
                      <span className="font-mono" data-testid="delivery-fee">+{gbp(deliveryFee)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between border-t border-black/10 pt-4">
                <span className="text-sm font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
                  Total Due
                </span>
                <span
                  data-testid="total-due"
                  className="font-display text-3xl font-extrabold text-[var(--brand-primary)]"
                >
                  {gbp(finalTotal)}
                </span>
              </div>
            </div>

            {/* Payment */}
            <div className="border border-black/10 bg-white p-6">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold">
                <Lock size={18} className="text-[var(--brand-primary)]" /> Payment
              </h2>
              {config?.square_enabled ? (
                <div className="mt-4">
                  <p className="mb-3 text-sm text-[var(--text-secondary)]">
                    Enter your card details to pay securely via Square.
                  </p>
                  <SquareCard
                    ref={cardRef}
                    appId={config.square_app_id}
                    locationId={config.square_location_id}
                    environment={config.square_environment}
                  />
                </div>
              ) : config && !config.square_enabled ? (
                <div className="mt-4 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="payment-test-mode">
                  <strong>Test mode:</strong> Square keys are not yet configured, so payment
                  is simulated. Add your Square credentials to enable live card checkout.
                </div>
              ) : null}

              <button
                onClick={submit}
                disabled={submitting || finalTotal <= 0}
                data-testid="submit-order-button"
                className="mt-5 flex w-full items-center justify-center gap-2 bg-[var(--brand-primary)] py-4 text-base font-semibold text-white transition-all hover:bg-[var(--brand-primary-hover)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Processing…
                  </>
                ) : (
                  <>Pay {gbp(finalTotal)} & Submit</>
                )}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[var(--text-secondary)]">
                <ShieldCheck size={13} /> Secure checkout powered by Square
              </p>
            </div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
